/**
 * TransactionDetailModal Component
 *
 * 거래 상세 정보 표시 및 수정/삭제 기능을 제공하는 모달
 *
 * 두 가지 모드:
 * - view: 거래 상세 정보 표시 ([닫기] [수정] [삭제] 버튼)
 * - edit: TransactionForm을 통한 거래 수정 ([취소] 버튼, 폼 내 저장 버튼)
 */

import { useState, useMemo } from 'react'
import { Modal } from '@/shared/components/ui/Modal'
import { useToast } from '@/shared/components/ui/Toast'
import { TransactionForm } from './TransactionForm'
import { useUpdateTransaction } from '../hooks/useUpdateTransaction'
import { useDeleteTransaction } from '../hooks/useDeleteTransaction'
import type { Transaction, TransactionInput } from '../types/Transaction'
import type { Category } from '@/domains/category/types/Category'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useCategories } from '@/domains/category/hooks/useCategories'

type ModalMode = 'view' | 'edit'

interface TransactionDetailModalProps {
  isOpen: boolean
  transaction: Transaction | null
  onClose: () => void
  onDeleted?: () => void
}

export function TransactionDetailModal({
  isOpen,
  transaction,
  onClose,
  onDeleted,
}: TransactionDetailModalProps) {
  const toast = useToast()
  const [mode, setMode] = useState<ModalMode>('view')

  const { updateTransaction, loading: updating } = useUpdateTransaction()
  const { deleteTransaction, loading: deleting } = useDeleteTransaction()

  // 관련 데이터 조회
  const { paymentMethods } = usePaymentMethods()
  const { users } = useUsers()
  const { expenseCategories, incomeCategories } = useCategories()

  // 카테고리 flat 배열 생성 (대분류 + 소분류 모두 포함)
  const allCategories = useMemo(() => {
    const result: Category[] = []
    for (const major of [...expenseCategories, ...incomeCategories]) {
      result.push(major)
      if (major.children) {
        result.push(...major.children)
      }
    }
    return result
  }, [expenseCategories, incomeCategories])

  if (!transaction) return null

  // 관련 데이터 조회
  const category = allCategories.find(c =>
    c.id === (transaction.minorCategoryId || transaction.majorCategoryId)
  )
  const paymentMethod = paymentMethods.find(pm => pm.id === transaction.paymentMethodId)
  const spender = users.find((u) => u.id === transaction.spenderId)

  // 카테고리 경로 (대분류 > 소분류)
  const majorCategory = allCategories.find(c => c.id === transaction.majorCategoryId)
  const categoryPath = transaction.minorCategoryId
    ? `${majorCategory?.name} > ${category?.name}`
    : category?.name

  /**
   * 수정 핸들러
   */
  const handleUpdate = async (input: TransactionInput) => {
    try {
      await updateTransaction(transaction.id, input)
      onClose()
      window.location.reload() // 페이지 리로드
    } catch (err) {
      toast.error(`수정 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 삭제 핸들러
   */
  const handleDelete = async () => {
    if (!confirm('정말 이 거래를 삭제하시겠습니까?')) return

    try {
      await deleteTransaction(transaction.id)
      onClose()
      onDeleted?.()
      window.location.reload() // 페이지 리로드
    } catch (err) {
      toast.error(`삭제 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 모달 닫기 핸들러
   * view 모드로 초기화 후 닫기
   */
  const handleClose = () => {
    setMode('view')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'view' ? '거래 상세' : '거래 수정'}
      size="md"
    >
      {mode === 'view' ? (
        <div className="space-y-4">
          {/* 메모 + 아이콘 */}
          <div className="flex items-center gap-3">
            <span className="text-3xl">{category?.icon || '❓'}</span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {transaction.memo || '(메모 없음)'}
              </h3>
              <p
                className={`text-2xl font-bold ${
                  transaction.type === 'income' ? 'text-income-fixed' : 'text-expense-fixed'
                }`}
              >
                {transaction.type === 'income' ? '+' : '-'}
                {transaction.amount.toLocaleString()}원
              </p>
            </div>
          </div>

          {/* 상세 정보 */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* 날짜 */}
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400 w-20">날짜</span>
              <span className="text-gray-900 dark:text-gray-100">{transaction.date}</span>
            </div>

            {/* 결제수단 (수입 등 결제수단이 없는 거래는 표시하지 않음) */}
            {paymentMethod && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400 w-20">결제수단</span>
                <span className="text-gray-900 dark:text-gray-100">
                  {paymentMethod.name}
                </span>
              </div>
            )}

            {/* 결제 계좌 소유자 (지출) / 받은 사람 (수입) */}
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400 w-20 shrink-0 leading-tight">
                {transaction.type === 'income' ? '받은 사람' : '결제 계좌 소유자'}
              </span>
              <span className="text-gray-900 dark:text-gray-100">
                {transaction.spenderId === 'user_shared'
                  ? '👥 공용'
                  : spender?.name || '알 수 없음'}
              </span>
            </div>

            {/* 카테고리 */}
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400 w-20">카테고리</span>
              <span className="text-gray-900 dark:text-gray-100">{categoryPath}</span>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleClose}
              disabled={deleting}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              닫기
            </button>
            <button
              onClick={() => setMode('edit')}
              disabled={deleting}
              className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              수정
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? '삭제 중...' : '삭제'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* 수정 폼 */}
          <TransactionForm
            initialType={transaction.type}
            initialData={{
              date: transaction.date,
              amount: transaction.amount,
              memo: transaction.memo,
              majorCategoryId: transaction.majorCategoryId,
              minorCategoryId: transaction.minorCategoryId || '',
              paymentMethodId: transaction.paymentMethodId,
              spenderId: transaction.spenderId,
            }}
            onSubmit={handleUpdate}
            onCancel={() => setMode('view')}
            submitLabel="저장"
            loading={updating}
          />
        </div>
      )}
    </Modal>
  )
}
